import React from 'react';
import dayjs from 'dayjs';
import ICSChecklist from '../IAP/ICSChecklist';
import { ActionRow } from './hooks/useScopedActionForm';
import { useIncidentObjectives } from '../IAP/hooks/incidentObjectiveHooks';
import { useIncidentRoster } from '../IAP/hooks/incidentRoster/useIncidentRoster';

interface ActionExpandedRowProps {
  row: ActionRow;
}

const formatDate = (date: Date | string | null | undefined) =>
  date && dayjs(date).isValid() ? dayjs(date).format('MM/DD/YYYY HH:mm') : '—';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="d-flex flex-column me-4 mb-2">
    <div className="text-muted small mt-1 mb-1">{label}</div>
    <span>{children}</span>
  </div>
);

const ActionExpandedRow: React.FC<ActionExpandedRowProps> = ({ row }) => {
  const checklist = row.checklist ?? [];
  // Resolve the POC guid to a name via the incident roster (raw GUIDs must
  // never face the user); fall back to the row's denormalized names.
  const { data: roster } = useIncidentRoster();
  const rosterPoc = row.poc
    ? roster?.find((m) => m.userId === row.poc)
    : undefined;
  const pocName =
    rosterPoc?.userName ||
    rosterPoc?.emailAddress ||
    (row as any).poc_user_name ||
    (row as any).user_name ||
    (row.poc ? 'Unknown member' : '—');
  // Resolve the aligned objective (parent_id) to its name so AI-aligned
  // actions are verifiable here (PRAT-5710). Disabled outside an incident.
  const { data: objectives } = useIncidentObjectives();
  const objectiveName = row.parent_id
    ? objectives?.find((o) => o.objective_id === row.parent_id)?.name
    : undefined;

  return (
    <div className="p-2">
      <div className="d-flex mb-2 flex-row flex-wrap">
        <Field label="OBJECTIVE">{objectiveName || '—'}</Field>
        <Field label="POINT OF CONTACT">{pocName}</Field>
        <Field label="BRIEFED POC">{row.briefed_poc ? 'Yes' : 'No'}</Field>
        <Field label="STATUS">{row.status || '—'}</Field>
        <Field label="START DATE">{formatDate(row.start_date)}</Field>
        <Field label="TARGET DATE">{formatDate(row.target_date)}</Field>
        <Field label="ACTUAL DATE">{formatDate(row.actual_date)}</Field>
      </div>
      {checklist.length > 0 && (
        <ICSChecklist checklist={checklist} setChecklist={() => {}} />
      )}
    </div>
  );
};

export default ActionExpandedRow;
